import { FC, Fragment, useMemo, useState } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { Badge, KeyValueTable, TableToggleField } from "@vilocnv/allsetra-core";
import AssignedUsersTable from "./children/AssignedUsersTable";
import ObjectDetailsCard from "components/cards/ObjectDetailsCard/ObjectDetailsCard";
import {
  ObjectDetailsLeftContainer,
  SmallText
} from "components/cards/ObjectDetailsCard/ObjectDetailsCard.styled";
import ObjectDetailsMap from "./children/ObjectDetailsMap";
import DevicesTable from "./children/DevicesTable";
import DataValuesAndNumber from "./children/DataValuesAndNumber";
import MileageCorrectionForm from "components/forms/objects/MileageCorrectionForm/MileageCorrectionForm";
import WarningIcon from "@mui/icons-material/Warning";

// DATA
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "hooks";
import {
  transformObjectForObjectInfoTable,
  transformObjectForReminderTable,
  transformObjectForWorkingHoursTable,
  transformObjectForAlarmConfigTable,
  transformObjectForCorrectionTable,
  transformObjectOwnersDataForTable,
  getSplittedAddress,
  handleIsPrivateRide
} from "app/data/helpers";
import { selectDrawerSelectedAccountId } from "app/data/selectors";
import { updateObjectAwayModeThunk } from "app/features/objects/objectsActions";
import { setAwayModeStatus } from "app/features/objects/objectsSlice";
import {
  LocationTitle,
  RecordKey,
  RecordValue,
  RecordsWrapper,
  ObjectContainer,
  RadiusSelectionBox
} from "./ObjectDetailsSection.styled";
import { HiddenBadgeIcon } from "components/ui/icons";

interface Props {
  specificObject: any | null;
  alarmsConfig: any;
}

const ObjectDetailsSection: FC<Props> = ({ specificObject, alarmsConfig }) => {
  const dispatch = useAppDispatch();
  const drawerSelectedAccountId = useAppSelector(selectDrawerSelectedAccountId);
  const [isUpdating, setIsUpdating] = useState(false);
  const awayModeStatus = useAppSelector(
    (state) =>
      state.rootReducer.objectsReducer.awayModeStatus[
        specificObject?.uniqueId
      ] ?? false
  );

  const { t } = useTranslation([
    "translation",
    "tableHeadingsTranslation",
    "formFieldsTranslation"
  ]);

  const handleAwayModeChange = async (checked: boolean) => {
    if (specificObject?.uniqueId && drawerSelectedAccountId) {
      setIsUpdating(true);
      dispatch(
        setAwayModeStatus({
          objectId: specificObject.uniqueId,
          isEnabled: checked
        })
      );

      try {
        await dispatch(
          updateObjectAwayModeThunk({
            accountId: drawerSelectedAccountId,
            objectId: specificObject.uniqueId,
            isEnabled: checked
          })
        ).unwrap();
      } catch (error) {
        dispatch(
          setAwayModeStatus({
            objectId: specificObject.uniqueId,
            isEnabled: !checked
          })
        );
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const { objectInformation, workingHours, alarmConfiguration, owners } =
    useMemo(() => {
      return {
        objectInformation: transformObjectForObjectInfoTable(specificObject),
        workingHours: transformObjectForWorkingHoursTable(specificObject),
        correction: transformObjectForCorrectionTable(specificObject, t),
        reminder: transformObjectForReminderTable(specificObject, t),
        alarmConfiguration: transformObjectForAlarmConfigTable(alarmsConfig),
        owners: transformObjectOwnersDataForTable(specificObject)
      };
    }, [specificObject, t]);

  const pzSwitchMetadata = handleIsPrivateRide(specificObject);

  return (
    <ObjectContainer>
      <Grid container spacing={4}>
        <Grid item xs={12} lg={5}>
          <ObjectDetailsLeftContainer spacing={4} mt={4}>
            <Stack spacing={2}>
              <RadiusSelectionBox>
                <Stack spacing={1} sx={{ width: "100%" }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    sx={{ width: "100%" }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1" alignItems="center">
                        {t("titles.awayMode")}
                      </Typography>
                      {awayModeStatus && (
                        <WarningIcon
                          color="warning"
                          sx={{ width: "16px", height: "16px" }}
                        />
                      )}
                    </Box>
                    <TableToggleField
                      label={""}
                      name="isAwayMode"
                      onChange={(e) =>
                        !isUpdating && handleAwayModeChange(e.target.checked)
                      }
                      checked={awayModeStatus}
                    />
                  </Box>
                  {awayModeStatus && (
                    <SmallText
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        maxWidth: "300px"
                      }}
                    >
                      {t("descriptions.awayModeDescription")}
                    </SmallText>
                  )}
                </Stack>
              </RadiusSelectionBox>
            </Stack>
            {specificObject && <ObjectDetailsCard object={specificObject} />}
            <LocationTitle>{t("titles.location")}</LocationTitle>
            <RecordsWrapper>
              <RecordKey>
                {t("address.label", {
                  ns: "formFieldsTranslation"
                })}
              </RecordKey>
              <Box>
                {pzSwitchMetadata && pzSwitchMetadata.value === "False" ? (
                  <RecordValue>
                    <Badge
                      colorScheme="blank"
                      icon={<HiddenBadgeIcon />}
                      textWeight="light"
                    >
                      {t("typography.addressIsHidden")}
                    </Badge>
                  </RecordValue>
                ) : (
                  <Fragment>
                    <RecordValue>
                      {getSplittedAddress(
                        0,
                        specificObject?.location?.resolvedAddress
                      )}
                    </RecordValue>
                    <RecordValue>
                      {getSplittedAddress(
                        1,
                        specificObject?.location?.resolvedAddress
                      ) +
                        getSplittedAddress(
                          2,
                          specificObject?.location?.resolvedAddress
                        )}
                    </RecordValue>
                  </Fragment>
                )}
              </Box>
            </RecordsWrapper>

            {/* <KeyValueTable
              title={t("titles.location")}
              records={{
                [t("address.label", {
                  ns: "formFieldsTranslation"
                })]: `${getAddress(specificObject?.location?.resolvedAddress)}`
              }}
            /> */}

            <AssignedUsersTable specificObject={specificObject} />

            <KeyValueTable
              title={t("titles.objectInfo")}
              records={objectInformation}
            />

            <DataValuesAndNumber
              data={specificObject?.accounts ?? []}
              dataType="accounts"
            />

            <KeyValueTable records={owners} />

            <DataValuesAndNumber
              data={specificObject?.groups ?? []}
              dataType={t("drawerMenuLinks.groups")}
            />
            {specificObject?.isWorkingHoursOverriden ? (
              <KeyValueTable
                title={t("titles.workingHours")}
                records={workingHours}
                customizedWorkingHours={true}
              />
            ) : (
              <KeyValueTable
                title={t("titles.workingHours")}
                records={workingHours}
              />
            )}

            <MileageCorrectionForm
              specificObject={specificObject}
              accountId={drawerSelectedAccountId ?? ""}
            />

            <DevicesTable specificObject={specificObject} />

            <KeyValueTable
              title={t("titles.alarmConfig")}
              records={alarmConfiguration}
              customizedWorkingHours={
                alarmsConfig?.objectHasCustomAlarmConfiguration
              }
            />
          </ObjectDetailsLeftContainer>
        </Grid>

        <Grid item xs={12} lg={7} gap={2}>
          <ObjectDetailsMap object={specificObject} />
        </Grid>
      </Grid>
    </ObjectContainer>
  );
};

export default ObjectDetailsSection;
